import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from 'i18n/i18n.types';
import { FormTemplate, FormTemplateSection, FormTemplateValidator } from 'data/template/template.types';
import * as types from 'data/network/network.types';
import Form, {
  FieldUpdateHandlerParam,
} from 'shared/form';
import './network-form.scss';
import { FormLayout } from 'components/FormLayout';
import { PORT_MAX_VALUE, validatePortNumberRange } from 'routes/Network/utils/validatePortNumberRange';
import FieldDesc from 'components/FieldDesc';

type Props = {
  template: FormTemplate,
  data: Partial<types.Network>
  onFieldUpdate?: (param: FieldUpdateHandlerParam) => void
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.Network
}

export const NEWORK_FORM_I18N_KEY = 'network.form';

export const NetworkForm = forwardRef<Ref, Props>(({
  template,
  data,
  onFieldUpdate = () => null,
}: Props, ref) => {
  const mainRef = useRef<Form>(null);

  const isValid = (forceValidation = false, forceTouched = true) => {
    const isMainValid = mainRef.current?.isValid(forceValidation, forceTouched) ?? true;
    return isMainValid;
  };

  const getValues = () => {
    const {
      networkIdHint,
      ...values
    } = mainRef.current?.getValues();

    const formValues: types.Network = {
      ...values,
    };

    return formValues;
  };

  const { t } = useTranslation();

  const portNumberRangeValidator = (
    _propFields?: unknown,
    _stateFields?: unknown,
    _value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
  ) => {
    const { startPortNumber, portNumberRange } = getValues();

    const valid = validatePortNumberRange(+portNumberRange, +startPortNumber);

    let message = '';
    if (!valid && validator?.customValidationFunction) {
      const portValue = Math.min(PORT_MAX_VALUE, PORT_MAX_VALUE + 1 - +startPortNumber).toString();
      message = t(`${NEWORK_FORM_I18N_KEY}.fieldError.${validator?.customValidationFunction}` as TranslationKey, { end: portValue }) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  };

  const customValidationFunctionMap = {
    portNumberRangeValidator,
  };

  const customFieldComponentMap = {
    networkIdHint: FieldDesc,
  };

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
  }));

  return (
    <div className="network-form" data-testid="NetworkForm">
      <FormLayout>
        <Form
          ref={mainRef}
          name={template.formId ?? template.key}
          defaults={data}
          fields={(template.main as FormTemplateSection).fields}
          layout={(template.main as FormTemplateSection).metadata.layout?.rows}
          fieldUpdateHandler={onFieldUpdate}
          customValidationFunctionMap={customValidationFunctionMap}
          customFieldComponentMap={customFieldComponentMap}
          i18nKeyPrefix={NEWORK_FORM_I18N_KEY}
        />
      </FormLayout>
    </div>
  );
});

export default NetworkForm;
